/**
 * @summary - Component that Staff Details
 * @author Purushothaman T
 * @since 2024-08-09
 */
import { Avatar, Box, Stack } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DefaultCard } from "../../assets/styled-components/card";
import { ErrorChips, SuccessChips } from "../../assets/styled-components/chips";
import {
  BemsGraph,
  FemsGraph,
  ifacSecurityURL,
  MasterGraph,
} from "../../utility/apiURLS";
import { staffdetails } from "../../utility/constants/masters";
import {
  getAuthToken,
  getcompanyId,
  getFacilityId,
} from "../../utility/tokenUtility";
import utils from "../../utility/utilities";
import CloseButton from "../commoncomponents/CloseButton";
import CreateNewBtn from "../commoncomponents/CreateNewBtn";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import PageTitle from "../commoncomponents/PageTitle";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import SectionTitle from "../commoncomponents/SectionTitle";
import ImagePopup from "./ImagePopup";
import CustomDialog from "../commoncomponents/CustomDialog";
import { dialogContent } from "../../utility/message";
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';


function StaffList() {
  const navigate = useNavigate();
  const column = staffdetails.gridColumn;
  const [rowData, setRowData] = useState();
  const [gridApi, setGridApi] = useState({});
  const [imageUrl, setImageUrl] = useState("");
  const [openImage, setOpenImage] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [contentDialog, setContentDialog] = useState("");
  const [staffData, setStaffData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGM002");

  useEffect(() => {
    fetchData();
  }, []);
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = getAuthToken("id_token");
      const facilityId = getFacilityId("facilityId");
      const companyId = getcompanyId("companyId");
      const config = {
        headers: {
          // Attach bearer token to request header
          Authorization: `Bearer ${token}`,
        },
      };
      const [employeeResponse, baseLocationResponse] = await Promise.all([
        axios.get(
          `${MasterGraph}/api/GraphMasemployeedetails/GetMasemployeedetailsByFacilityId/${facilityId}?size=1000`,
          config
        ),
        axios.get(
          `${ifacSecurityURL}/api/GraphUserdetails/Locationbasedoncompantid/${companyId}?size=1000`,
          config
        ),
      ]);

      setRowData(
        employeeResponse.data.map((data) => {
          return {
            ...data,
            isactive: data.status ? "Active" : "Inactive",
            effectivefrom: utils.formatDate({
              dateString: data.effectiveFrom,
              format: "dd/mm/yyyy",
              dbdate: true,
            }),
            profile: data.imagepath,
            location: baseLocationResponse?.data?.filter(
              (loc) => loc?.id === data?.locationId
            )[0]?.facilityname,
          };
        })
      );
    } catch (error) {
      console.error("Error fetching options:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const colDefs = [
    {
      field: "profile",
      headerName: "Profile Picture",
      sortable: false,
      filter: false,
      tooltipValueGetter: () => "Profile Picture",
      cellStyle: { textAlign: "center" },
      cellRenderer: ({ data }) => (
        <>
          {handleImage && (
            <Avatar
              src={data.profile}
              className="avatar-hover"
              style={{
                width: 50,
                height: 50,
                margin: "4px 0px",
                cursor: "pointer",
              }}
              onClick={() => handleImage(data)}
            />
          )}
        </>
      ),
    },
    {
      field: column.employeeId.field,
      headerName: column.employeeId.header,
    },
    {
      field: column.employeeName.field,
      headerName: column.employeeName.header,
    },
    {
      field: column.designation.field,
      headerName: column.designation.header,
    },
    {
      field: column.status.field,
      headerName: column.status.header,
      cellRenderer: (params) => {
        if (params.value === "Active") {
          return <SuccessChips label={params.value} />;
        } else {
          return <ErrorChips label={params.value} />;
        }
      },
    },
    {
      field: column.effectivefrom.field,
      headerName: column.effectivefrom.header,
    },
    {
      field: column.skillslevel.field,
      headerName: column.skillslevel.header,
    },
    {
      field: column.service.field,
      headerName: column.service.header,
    },
    {
      field: column.location.field,
      headerName: column.location.header,
    },
  ];

  const handleEdit = (data) => {
    navigate("/staffdetails/register", {
      state: {
        isEditMode: true,
        id: data?.id,
        userId : data?.userId
      },
    });
  };
  const handleView = (data) => {
    navigate("/staffdetails/view", {
      state: {
        isViewMode: true,
        id: data?.id,
        userId : data?.userId
      },
    });
  };
  const handleImage = (data) => {
    setImageUrl(data.profile);
    if (data.profile) {
      setOpenImage(true);
    } else {
      setOpenDialog(true);
      setContentDialog("Profile picture unavailable.");
    }
  };
  const handleCreateClick = () => {
    navigate("/staffdetails/register");
  };

  const handleDelete = (data) => {
    setOpenDeleteDialog(true);
    setStaffData(data);
  };

  const handleConfirmDelete = async () => {
    try {
      const token = getAuthToken("id_token");

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const [responseFems, responseBems] = await Promise.all([
        axios.get(
          `${FemsGraph}/api/EmployeeDetails/FemsCheckEmployeeAlreadyExists/${staffData.id}`,
          config
        ),
        axios.get(
          `${BemsGraph}/api/EmployeeDetails/BemsCheckEmployeeAlreadyExists/${staffData.id}`,
          config
        ),
      ]);
      if (responseFems.data === 0 && responseBems.data === 0) {
        const responseMaster = await axios.delete(
          `${MasterGraph}/api/masemployeedetails/${staffData.id}`,
          config
        );
        if (responseMaster.status === 204) {
          setOpenDialog(true);
          setContentDialog(dialogContent.deleted);
          fetchData();
        }
      } else {
        setOpenDialog(true);
        setOpenDeleteDialog(false);
        setContentDialog("Staff was in use. Cannot be deleted.");
      }
    } catch (error) {
      if (error.response.status === 400) {
        setOpenDialog(true);
        setContentDialog(error.response.data.detail);
      }
      return error.response;
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setOpenDeleteDialog(false);
  };

  return (
    <>
      <PageTitle title={"Staff Details"} />
      <DefaultCard>
        <Stack mb={1} direction={"row"} justifyContent={"space-between"}>
          <SectionTitle title={"Staff Details List"} />
          <Stack direction={"row"} spacing={1.5}>
            {access?.canCreate && <CreateNewBtn onClick={handleCreateClick} />}
            <RefreshExportButtons
              pagination={false}
              exportFunction={() => { }}
              exportData={rowData}
              refData={gridApi}
              handleRefresh={fetchData}
              title={"Staff Details"}
            />
          </Stack>
        </Stack>
        <Box>
          <CustomAgGrid
            handleImage={handleImage}
            rows={rowData}
            columns={colDefs}
            refSet={gridApi}
            setGridApi={setGridApi}
            isDateComparator={true}
            handleEdit={access?.canEdit && handleEdit}
            handleView={access?.canView && handleView}
            imageHeader={"Profile Picture"}
            isLoading={isLoading}
            handleDelete={access?.canDelete && handleDelete}
          />
        </Box>
        <ImagePopup
          open={openImage}
          onClose={() => setOpenImage(false)}
          imageUrl={imageUrl}
        />
        <CustomDialog
          confirmBtn={"Ok"}
          open={openDialog}
          content={contentDialog}
          onConfirm={handleDialogClose}
        />
        <CustomDialog
          open={openDeleteDialog}
          onConfirm={handleConfirmDelete}
          content={dialogContent.confirmDelete}
          cancelBtn={"No"}
          confirmBtn={"Yes"}
          onClose={handleDialogClose}
        />
      </DefaultCard>
      <CloseButton />
    </>
  );
}

export default StaffList;
